import React, { useEffect, useState, memo } from 'react';
import { Text } from '@cuemath/web-comps';
import { Separator, Spinner } from '@cuemath/comps';

import {
  useFunnelSubSourceDetail,
  useFunnelMonthSubSourceDetail,
  useFunnelAllPagesDetail,
} from '../api/get-funnel';
import KpiCards from '../components/kpi-cards';
import PeriodChips from '../components/period-chips';
import TopSeriesGraphCard from '../components/top-series-graph';
import FilterDropdown from 'comps/filter/new-filter-dropdown';
import FunnelTable, { buildContributorColumns, buildPageLandingColumns } from '../components/funnel-table';
import {
  Card,
  SectionLabel,
  StateBlock,
  LoadingOverlay,
  FilterField,
  FieldLabelText,
  UI,
} from '../styled-comps';
import {
  METRICS,
  METRIC_LABELS,
  EMPTY_MESSAGE,
  TOP_PAGES_COUNT,
  ncPairLabel,
  UNKNOWN_REASONS,
} from '../constants';
import {
  asRows,
  metricFields,
  sumRows,
  channelLabel,
  buildRangeParams,
  buildMonthlyTopSeries,
} from '../helpers';
import { useSettledKey } from '../hooks/use-settled-key';
import { usePeriodSelection } from '../hooks/use-period-selection';
import { useGraphControls } from '../hooks/use-graph-controls';

// Merge (source x platform) rows that map to the same friendly label, and keep
// the raw pairs behind each label so the deep-dive can filter to exactly them.
const aggregateByLabel = rows => {
  const byLabel = {};
  rows.forEach(row => {
    const label = ncPairLabel(row.sub_source, row.platform);
    if (!byLabel[label]) {
      byLabel[label] = { label, subSources: [], platforms: [], ...metricFields({}) };
    }
    const bucket = byLabel[label];
    if (bucket.subSources.indexOf(row.sub_source) === -1) bucket.subSources.push(row.sub_source);
    if (bucket.platforms.indexOf(row.platform) === -1) bucket.platforms.push(row.platform);
    const fields = metricFields(row);
    Object.keys(fields).forEach(key => {
      bucket[key] += fields[key];
    });
  });
  return Object.values(byLabel).sort((a, b) => b[METRICS.QL] - a[METRICS.QL]);
};

const DEFAULT_DRILL = 'Website signups';

// Non-Content tab v3: ALL sub-sources (sheet-comparable), classified into
// readable groups (user_source_log source_channel x platform_channel), plus a
// single-select deep-dive: pick any sub-source group and see the landing pages
// of its leads.
const NonContent = memo(({ channel }) => {
  // ── table/cards section state: month-or-range period + region/user-type ──
  const { periodProps, window: tableWindow, label: periodLabel, regions, countries, userTypes } =
    usePeriodSelection();
  const [drillLabel, setDrillLabel] = useState(DEFAULT_DRILL);

  // ── graph section state (independent, its own Apply) ──
  const {
    graphProps,
    metric: graphMetric,
    appliedYear: graphYear,
    appliedRegions: graphRegions,
    appliedCountries: graphCountries,
    appliedUserTypes: graphUserTypes,
  } = useGraphControls();

  const {
    data: detailData,
    get: getDetail,
    isProcessing: detailLoading,
    error: detailError,
  } = useFunnelSubSourceDetail();
  const {
    data: monthDetailData,
    get: getMonthDetail,
    isProcessing: graphLoading,
    error: graphError,
  } = useFunnelMonthSubSourceDetail();
  const {
    data: landingData,
    get: getLandingPages,
    isProcessing: landingLoading,
    error: landingError,
  } = useFunnelAllPagesDetail();

  const tableQuery = buildRangeParams(tableWindow.start, tableWindow.end, {
    channel,
    regions,
    countries,
    userTypes,
  });
  const tableKey = JSON.stringify(tableQuery);
  useEffect(() => {
    getDetail('wm-nc-detail', tableQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey]);

  // True only once each response reflects its applied query — so neither the
  // sub-source table/cards nor the deep-dive render the previous period's (or
  // previous drill's) rows under a freshly-changed heading.
  const freshDetail = useSettledKey(tableKey, detailLoading, detailError);

  // ── table/cards derivation ──
  const subSourceRows = aggregateByLabel(asRows(freshDetail ? detailData : []));
  const totals = sumRows(subSourceRows);

  // Deep-dive: landing pages of the SELECTED sub-source group only. The label
  // maps back to its exact (source x platform) pairs from the current table.
  const drillOptions = subSourceRows.map(row => ({ label: row.label, value: row.label }));
  const activeDrill =
    subSourceRows.find(row => row.label === drillLabel) ||
    subSourceRows.find(row => row.label === DEFAULT_DRILL) ||
    subSourceRows[0];
  const drillKey = activeDrill ? `${tableKey}|${activeDrill.label}` : '';
  useEffect(() => {
    if (activeDrill) {
      getLandingPages('wm-nc-pairs', {
        ...tableQuery,
        sub_sources: activeDrill.subSources,
        platforms: activeDrill.platforms,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillKey]);

  const freshDrill = useSettledKey(drillKey, landingLoading, landingError);
  const landingRows = (freshDrill ? asRows(landingData) : [])
    .map(row => ({ label: row.page, landing: row.landing_page, ...metricFields(row) }))
    .sort((a, b) => b[METRICS.QL] - a[METRICS.QL]);

  const graphQuery = buildRangeParams(
    new Date(graphYear, 0, 1),
    new Date(graphYear, 11, 31),
    { channel, regions: graphRegions, countries: graphCountries, userTypes: graphUserTypes },
  );
  const graphKey = JSON.stringify(graphQuery);
  useEffect(() => {
    getMonthDetail('wm-nc-month-detail', graphQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphKey]);

  // ── graph derivation: top sub-source groups, month-on-month ──
  const monthRows = asRows(monthDetailData);
  const graphDatasets = buildMonthlyTopSeries(
    monthRows,
    graphYear,
    graphMetric,
    row => ncPairLabel(row.sub_source, row.platform),
  );

  const isFetching = detailLoading || graphLoading || landingLoading;

  return (
    <>
      {isFetching && (
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      )}
      <SectionLabel>{channelLabel(channel)} — pick a period</SectionLabel>
      <PeriodChips {...periodProps} />

      <SectionLabel>{`${channelLabel(channel)} — ${periodLabel}`}</SectionLabel>
      {detailError ? (
        <StateBlock>
          <Text variant="body1" color={UI.down}>{detailError}</Text>
        </StateBlock>
      ) : (detailLoading || !freshDetail) && !subSourceRows.length ? (
        <StateBlock>
          <Text variant="body1" color={UI.muted}>Loading {channelLabel(channel)} metrics…</Text>
        </StateBlock>
      ) : (
        <>
          <KpiCards totals={totals} compareTotals={null} />

          <SectionLabel>
            {`All sub-sources (${subSourceRows.length}) — rows sum to the totals above (sheet-comparable)`}
          </SectionLabel>
          <FunnelTable
            data={subSourceRows}
            columns={buildContributorColumns('Sub-source', UNKNOWN_REASONS.SUB_SOURCE)}
            loading={detailLoading}
            defaultSorted={[{ id: METRICS.QL, desc: true }]}
            emptyMessage={EMPTY_MESSAGE}
            showTotals
          />
        </>
      )}

      <SectionLabel>
        {`Deep dive — landing pages of one sub-source group (${periodLabel})`}
      </SectionLabel>
      <Card>
        <FilterField $width="280px">
          <FieldLabelText variant="eyebrow2">Sub-source to deep dive</FieldLabelText>
          <FilterDropdown
            options_list={drillOptions}
            filter={{ value: activeDrill ? activeDrill.label : '' }}
            onChange={value => setDrillLabel(value || DEFAULT_DRILL)}
          />
        </FilterField>
        <Separator height={14} transparent />
        {landingError ? (
          <Text variant="body2" color={UI.down}>{landingError}</Text>
        ) : (
          <FunnelTable
            data={landingRows}
            columns={buildPageLandingColumns()}
            loading={landingLoading || (!!activeDrill && !freshDrill)}
            defaultSorted={[{ id: METRICS.QL, desc: true }]}
            emptyMessage={EMPTY_MESSAGE}
            showTotals
          />
        )}
        <Separator height={6} transparent />
        <Text variant="caption" color={UI.muted}>
          {activeDrill
            ? `Signup page x landing page pairs; the landing page (signup session) is what decided the category (source: ${activeDrill.subSources.join(', ')} · platform: ${activeDrill.platforms.join(', ')})`
            : ''}
        </Text>
      </Card>

      <TopSeriesGraphCard
        {...graphProps}
        sectionLabel={`Sub-sources — month on month (${graphYear})`}
        caption={`Showing ${METRIC_LABELS[graphMetric]} per month for the top ${TOP_PAGES_COUNT} sub-source groups of ${graphYear}. Chips here don't affect the sections above.`}
        datasets={graphDatasets}
        hasRawData={!!monthRows.length}
        loading={graphLoading}
        error={graphError}
      />
    </>
  );
});

export default NonContent;
